import { Box, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import { TableCell } from "@shared/components";

import { CategoryMedal } from "@modules/financial/components/CategoryMedal";
import { ClientsCountCell } from "@modules/financial/components/ClientsCountCell";
import {
  formatNfeAmount,
  formatNfeStatus,
  getNfeIntegration,
  nfeActionKey,
  toNfeFileParams,
  toNfeNoteParams,
} from "@modules/financial/nfe";
import { ClientsByCategoryRow, FinancialFilterValues, FinancialTab, NfeRow } from "@modules/financial/types";

export interface NfeColumnActions {
  integration: string;
  filters: FinancialFilterValues;
  onDetail: (row: NfeRow) => void;
  onDownload: (row: NfeRow) => void;
  downloadingKey: string | null;
}

export interface ClientsCountColumnActions {
  listParams: Record<string, string | number>;
  writeParams: Record<string, string | number>;
}

type Translate = (key: string) => string;

function textColumn(field: string, headerName: string, minWidth = 160): GridColDef {
  return {
    field,
    headerName,
    minWidth,
    flex: 1,
    sortable: false,
    renderCell: ({ value }) => <TableCell.Text value={value ?? "—"} textAlign="left" />,
  };
}

function numberColumn(field: string, headerName: string, minWidth = 120): GridColDef {
  return {
    field,
    headerName,
    minWidth,
    headerAlign: "center",
    sortable: false,
    renderCell: ({ value }) => <TableCell.Text value={value ?? "—"} textAlign="center" />,
  };
}

export function getFinancialColumns(
  tab: FinancialTab,
  translate: Translate,
  nfeActions?: NfeColumnActions,
  clientsCount?: ClientsCountColumnActions
): GridColDef[] {
  const agreement = textColumn("agreement_name", translate("financial_admin.fields.agreement"));
  const slug = textColumn("plan_slug_integration", translate("financial_admin.fields.plan_slug_integration"), 180);
  const plan = textColumn("plan_name", translate("financial_admin.fields.plan"));
  const product = textColumn("product_name", translate("financial_admin.fields.product"));
  const level: GridColDef = {
    field: "level_name",
    headerName: translate("financial_admin.fields.level"),
    minWidth: 180,
    flex: 1,
    sortable: false,
    renderCell: ({ value }) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, width: "100%" }}>
        <CategoryMedal name={value} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <TableCell.Text value={value ?? "—"} textAlign="left" />
        </Box>
      </Box>
    ),
  };
  const rank = numberColumn("level_rank", translate("financial_admin.fields.level_rank"), 90);

  if (tab === "clients_by_plan") {
    return [agreement, slug, plan, product, level, numberColumn("clients_count", translate("financial_admin.fields.clients_count"))];
  }

  if (tab === "client_plans") {
    return [
      textColumn("cpf", translate("financial_admin.fields.cpf"), 140),
      textColumn("user_name", translate("financial_admin.fields.user")),
      agreement,
      slug,
      plan,
      product,
      level,
      rank,
    ];
  }

  if (tab === "clients_by_category") {
    return [
      level,
      rank,
      {
        field: "clients_count",
        headerName: translate("financial_admin.fields.clients_count"),
        minWidth: 360,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) =>
          clientsCount ? (
            <ClientsCountCell
              row={row as ClientsByCategoryRow}
              listParams={clientsCount.listParams}
              writeParams={clientsCount.writeParams}
            />
          ) : (
            <TableCell.Text value={row.clients_count ?? "—"} textAlign="center" />
          ),
      },
    ];
  }

  if (tab === "plans_by_category") {
    return [level, rank, numberColumn("plans_count", translate("financial_admin.fields.plans_count"))];
  }

  if (tab === "nfe") {
    const integration = getNfeIntegration(nfeActions?.integration);
    const columns: GridColDef[] = [
      agreement,
      textColumn("numero_nf", translate("financial_admin.fields.numero_nf"), 120),
      textColumn("serie", translate("financial_admin.fields.serie"), 80),
      textColumn("modelo", translate("financial_admin.fields.modelo"), 100),
      {
        field: "valor",
        headerName: translate("financial_admin.fields.valor"),
        minWidth: 120,
        sortable: false,
        renderCell: ({ value }) => <TableCell.Text value={formatNfeAmount(value)} textAlign="right" />,
      },
      {
        field: "data_emissao",
        headerName: translate("financial_admin.fields.data_emissao"),
        minWidth: 140,
        sortable: false,
        renderCell: ({ value }) => <TableCell.Date value={value} showTime={false} />,
      },
      textColumn("client_name", translate("financial_admin.fields.cliente"), 180),
      textColumn("cpf", translate("financial_admin.fields.cpf"), 150),
      {
        field: "status",
        headerName: translate("financial_admin.fields.status"),
        minWidth: 160,
        sortable: false,
        renderCell: ({ value }) => (
          <TableCell.Text value={formatNfeStatus(value, translate)} textAlign="left" />
        ),
      },
    ];

    if (integration?.extraColumns.includes("tipo")) {
      columns.push(textColumn("tipo", translate("financial_admin.fields.tipo"), 110));
    }
    if (integration?.extraColumns.includes("id_fatura")) {
      columns.push(textColumn("id_fatura", translate("financial_admin.fields.fatura"), 140));
    }
    if (nfeActions && integration && (integration.detail || integration.download)) {
      columns.push({
        field: "actions",
        headerName: translate("table.actions"),
        headerAlign: "center",
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => {
          const note = row as NfeRow;
          const canDetail = toNfeNoteParams(note, nfeActions.filters) !== null;
          const canDownload = toNfeFileParams(note) !== null;
          const downloading = nfeActions.downloadingKey === nfeActionKey(note);

          return (
            <TableCell.Container align="center">
              {canDetail && (
                <Tooltip title={translate("financial_admin.actions.detail")}>
                  <IconButton size="small" onClick={() => nfeActions.onDetail(note)}>
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDownload && (
                <Tooltip title={translate("financial_admin.actions.download")}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={downloading}
                      onClick={() => nfeActions.onDownload(note)}
                    >
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </TableCell.Container>
          );
        },
      });
    }

    return columns;
  }

  return [
    textColumn("cpf", translate("financial_admin.fields.cpf"), 140),
    textColumn("user_name", translate("financial_admin.fields.user")),
    agreement,
    product,
    plan,
    slug,
    level,
    numberColumn("year", translate("financial_admin.fields.year"), 90),
    {
      field: "month",
      headerName: translate("financial_admin.fields.month"),
      minWidth: 110,
      sortable: false,
      renderCell: ({ value }) => (
        <TableCell.Text
          value={value ? translate(`financial_admin.months.${value}`) : "—"}
          textAlign="left"
        />
      ),
    },
    {
      field: "media_type",
      headerName: translate("financial_admin.fields.media_type"),
      minWidth: 150,
      sortable: false,
      renderCell: ({ value }) => (
        <TableCell.Text
          value={value ? translate(`financial_admin.media_types.${value}`) : "—"}
          textAlign="left"
        />
      ),
    },
    numberColumn("received_count", translate("financial_admin.fields.received_count")),
    numberColumn("expected_count", translate("financial_admin.fields.expected_count")),
  ];
}
